import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Euro, PiggyBank, Activity, Eye,
  BarChart3, Calendar, Filter, ArrowUpRight, ArrowDownRight,
  Target, Clock, CreditCard, Percent, RefreshCw,
  AlertCircle, CheckCircle, TrendingDown, Gift, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { customerApi, DashboardData, PortfolioStats, CustomerInvestment, MonthlyData, CustomerTransaction } from '@/api/customers';
import toast from 'react-hot-toast';
import { BarGroupChart } from '@/components/shared/BarGroupChart';

const DashboardPage = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('12');
  const [showAllInvestments, setShowAllInvestments] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [monthlySeries, setMonthlySeries] = useState<MonthlyData[]>([]);
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(false);
  const [isJointAccount, setIsJointAccount] = useState(false);
  const [partnerName, setPartnerName] = useState<string | null>(null);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setError(null);
      const data = await customerApi.dashboard.getData();
      setDashboardData(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Fehler beim Laden der Dashboard-Daten';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching dashboard data:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        await fetchDashboardData();
        await loadMonthlySeries(parseInt(selectedPeriod));
        // Load profile to detect joint account
        try {
          const profile = await customerApi.profile.get();
          if ((profile as any).is_joint_account) {
            setIsJointAccount(true);
            const pFirst = (profile as any).partner_first_name || '';
            const pLast = (profile as any).partner_last_name || '';
            if (pFirst || pLast) setPartnerName(`${pFirst} ${pLast}`.trim());
          }
        } catch {}
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Load monthly series when selectedPeriod changes
  useEffect(() => {
    loadMonthlySeries(parseInt(selectedPeriod));
  }, [selectedPeriod]);

  const loadMonthlySeries = async (months: number) => {
    setIsMonthlyLoading(true);
    try {
      const series = await customerApi.dashboard.getMonthlyData(months);
      setMonthlySeries(series);
    } catch (e) {
      console.error(e);
    } finally {
      setIsMonthlyLoading(false);
    }
  };

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchDashboardData();
      toast.success('Daten wurden aktualisiert');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days remaining
  const calculateDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 3600 * 24));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Dashboard wird geladen…</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 bg-danger-50 border border-danger-200 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-6 h-6 text-danger-600" />
          </div>
          <h3 className="text-lg font-serif font-medium text-slate-900 mb-2">Daten konnten nicht geladen werden</h3>
          <p className="text-sm text-slate-500 mb-6">Beim Laden der Dashboard-Daten ist ein Fehler aufgetreten.</p>
          <Button onClick={refreshData}>Erneut versuchen</Button>
        </div>
      </div>
    );
  }

  // Extract data directly from dashboardData
  const {
    portfolio_stats,
    recent_investments,
    recent_transactions,
    monthly_data
  } = dashboardData;

  // Show all investments
  const allInvestments = recent_investments || [];

  const getEndDate = (inv: any) => inv.maturity_date || inv.end_date;

  const txTypeLabel = (t: string) => ({
    deposit: 'Einzahlung',
    bonus: 'Neukundenbonus',
    withdrawal: 'Auszahlung',
    interest_payment: 'Zinszahlung',
    refund: 'Erstattung'
  } as Record<string,string>)[t] || t

  // Bonus wird wie eine Einzahlung behandelt: Zugang, grün, mit Pluszeichen
  const isCredit = (t: string) => ['deposit', 'bonus', 'interest_payment'].includes(t)

  const txIconTone = (type: string) => {
    if (type === 'interest_payment') return { box: 'bg-success-50 border-success-200', icon: 'text-success-600' };
    if (type === 'withdrawal') return { box: 'bg-danger-50 border-danger-200', icon: 'text-danger-600' };
    if (type === 'bonus') return { box: 'bg-gold-50 border-gold-200', icon: 'text-gold-700' };
    if (type === 'deposit') return { box: 'bg-primary-50 border-primary-100', icon: 'text-primary-700' };
    return { box: 'bg-slate-100 border-slate-200', icon: 'text-slate-500' };
  };

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-danger-600 mr-3 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-danger-800">Fehler</h3>
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-1.5 block">Ihr Portfolio</span>
          <h1 className="text-2xl font-serif font-medium text-slate-900">
            Willkommen, {user?.name || 'geschätzte Kundin/geschätzter Kunde'}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <p className="text-slate-500 text-sm">Übersicht Ihres Anlageportfolios</p>
            {isJointAccount && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-primary-200 bg-primary-50 text-primary-700 text-xs font-semibold">
                Gemeinschaftskonto{partnerName ? ` · ${partnerName}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamtanlage</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">
                {formatCurrency(portfolio_stats.total_investment)}
              </p>
            </div>
            <div className="w-11 h-11 bg-primary-50 flex items-center justify-center shrink-0">
              <Euro className="w-5 h-5 text-primary-700" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamtgewinn bis jetzt</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">
                {formatCurrency(portfolio_stats.total_profit)}
              </p>
            </div>
            <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Portfoliowert</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">
                {formatCurrency(portfolio_stats.total_value)}
              </p>
            </div>
            <div className="w-11 h-11 bg-gold-50 flex items-center justify-center shrink-0">
              <PiggyBank className="w-5 h-5 text-gold-700" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rendite (Laufzeitende)</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">
                {formatCurrency((portfolio_stats as any).expected_interest ?? portfolio_stats.monthly_return)}
              </p>
            </div>
            <div className="w-11 h-11 bg-primary-50 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-primary-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Active Investments */}
        <div className="lg:col-span-2 bg-white border border-slate-200 border-t-2 border-t-primary-600">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-serif font-medium text-slate-900">Aktive Anlagen</h2>
              <Button variant="ghost" size="sm" onClick={()=> setShowAllInvestments(true)}>
                <Eye className="w-4 h-4 mr-2" />
                Alle anzeigen
              </Button>
            </div>
          </div>
          <div className="p-6">
            {allInvestments.length > 0 ? (
              <div className="space-y-3">
                {allInvestments.slice(0, 5).map((investment: any) => (
                  <div key={investment.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-white border border-primary-100 flex items-center justify-center overflow-hidden shrink-0">
                        {investment.bank_logo_url ? (
                          <img src={investment.bank_logo_url} alt="bank logo" className="w-10 h-10 object-contain" />
                        ) : (
                        <CreditCard className="w-5 h-5 text-primary-700" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-slate-900">
                          {investment.bank_name}
                          {investment.status === 'deposited' && (
                            <span className="ml-2 text-xs font-normal text-primary-700 bg-primary-50 border border-primary-200 px-1.5 py-0.5">Eingezahlt</span>
                          )}
                          {investment.status === 'pending' && (
                            <span className="ml-2 text-xs font-normal text-warning-700 bg-warning-50 border border-warning-200 px-1.5 py-0.5">Kapitaleingang ausstehend</span>
                          )}
                        </h3>
                        {investment.status !== 'deposited' && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {investment.start_date ? formatDate(investment.start_date) : '-'} - {getEndDate(investment) ? formatDate(getEndDate(investment)) : '-'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900 tabular-nums">
                        {formatCurrency(investment.amount)}
                      </p>
                      {investment.status === 'pending' && (
                        <p className="text-xs font-medium text-warning-700 mt-0.5">
                          Kapitaleingang ausstehend: {formatCurrency(investment.amount - investment.current_value)}
                        </p>
                      )}
                      {investment.status !== 'deposited' && (
                        <div className="flex items-center justify-end mt-1">
                          <span className="text-xs text-success-600 font-medium">
                            {parseFloat(String(investment.interest_rate)).toFixed(2)}% p.a.
                          </span>
                          {getEndDate(investment) && (
                            <span className="text-xs text-slate-400 ml-2">
                              {investment.duration_months || investment.term_months} Monate
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                  <PiggyBank className="w-5 h-5 text-slate-400" />
                </div>
                <h3 className="text-sm font-medium text-slate-900 mb-1">Noch keine Anlage</h3>
                <p className="text-sm text-slate-500">Ihre aktiven Anlagen werden hier angezeigt.</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-serif font-medium text-slate-900">Letzte Transaktionen</h2>
              <Button variant="ghost" size="sm" onClick={()=> setShowAllTransactions(true)}>
                <Eye className="w-4 h-4 mr-2" />
                Alle anzeigen
              </Button>
            </div>
          </div>
          <div className="p-6">
            {recent_transactions.length > 0 ? (
              <div className="space-y-4">
                {recent_transactions.slice(0, 5).map((transaction) => {
                  const tone = txIconTone(transaction.type);
                  return (
                  <div key={transaction.id} className="flex items-center justify-between">
                    <div className={`w-10 h-10 border flex items-center justify-center shrink-0 ${tone.box}`}>
                      {transaction.type === 'interest_payment' && <Euro className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'withdrawal' && <ArrowDownRight className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'deposit' && <ArrowUpRight className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'bonus' && <Gift className={`w-4 h-4 ${tone.icon}`} />}
                      {!['interest_payment', 'withdrawal', 'deposit', 'bonus'].includes(transaction.type) && (
                        <Activity className={`w-4 h-4 ${tone.icon}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 ml-3">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {transaction.description || txTypeLabel(transaction.type)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('de-DE') : 'Unbekanntes Datum'}
                      </p>
                    </div>
                    <div className={`text-sm font-medium tabular-nums ${
                      isCredit(transaction.type)
                        ? 'text-success-600' : transaction.type === 'withdrawal'
                        ? 'text-danger-600' : 'text-slate-600'
                    }`}>
                      {isCredit(transaction.type) && '+'}
                      {transaction.amount.toLocaleString('de-DE')} €
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                  <Activity className="w-5 h-5 text-slate-400" />
                </div>
                <h3 className="text-sm font-medium text-slate-900 mb-1">Keine Transaktionen</h3>
                <p className="text-sm text-slate-500">Ihre Transaktionen werden hier angezeigt.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Investment Performance Chart */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-serif font-medium text-slate-900">Anlageperformance</h2>
            <div className="flex items-center space-x-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="text-sm border border-slate-300 px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-slate-700"
              >
                <option value="6">Letzte 6 Monate</option>
                <option value="12">Letzte 12 Monate</option>
                <option value="24">Letzte 2 Jahre</option>
              </select>
            </div>
          </div>
        </div>
        <div className="p-6">
          {isMonthlyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary-100 border-t-primary-600"></div>
            </div>
          ) : monthlySeries && monthlySeries.length > 0 ? (
            <>
              <BarGroupChart data={monthlySeries.slice(-parseInt(selectedPeriod))} currency={formatCurrency} labels={{ investment: 'Anlage', profit: 'Gewinn', total: 'Summe' }} />
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-5 h-5 text-slate-400" />
              </div>
              <h3 className="text-sm font-medium text-slate-900 mb-1">Noch keine Daten</h3>
              <p className="text-sm text-slate-500">Tätigen Sie eine Anlage, um Leistungsdaten zu sehen.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAllInvestments && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-primary-950/50" onClick={()=> setShowAllInvestments(false)} />
          <div className="relative bg-white w-full max-w-4xl border border-slate-200 border-t-2 border-t-primary-600 max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-serif font-medium text-slate-900">Alle Anlagen</h3>
              <button onClick={()=> setShowAllInvestments(false)} className="text-slate-400 hover:text-slate-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-auto space-y-2">
              {allInvestments.length === 0 ? (
                <p className="text-slate-500 text-sm">Keine Einträge</p>
              ) : allInvestments.map((investment: any)=> (
                <div key={investment.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white border border-primary-100 flex items-center justify-center shrink-0">
                      <CreditCard className="w-4 h-4 text-primary-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {investment.bank_name}
                        {investment.status === 'deposited' && (
                          <span className="ml-2 text-xs font-normal text-primary-700 bg-primary-50 border border-primary-200 px-1.5 py-0.5">Eingezahlt</span>
                        )}
                        {investment.status === 'pending' && (
                          <span className="ml-2 text-xs font-normal text-warning-700 bg-warning-50 border border-warning-200 px-1.5 py-0.5">Kapitaleingang ausstehend</span>
                        )}
                      </p>
                      {investment.status !== 'deposited' && (
                        <p className="text-xs text-slate-500 mt-0.5">{investment.start_date ? formatDate(investment.start_date as string) : '-'} - {getEndDate(investment) ? formatDate(getEndDate(investment) as string) : '-'}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900 tabular-nums">{formatCurrency(investment.amount)}</p>
                    <p className="text-xs text-success-600 font-medium">{parseFloat(String(investment.interest_rate)).toFixed(2)}% p.a.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAllTransactions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-primary-950/50" onClick={()=> setShowAllTransactions(false)} />
          <div className="relative bg-white w-full max-w-3xl border border-slate-200 border-t-2 border-t-gold-500 max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-serif font-medium text-slate-900">Alle Transaktionen</h3>
              <button onClick={()=> setShowAllTransactions(false)} className="text-slate-400 hover:text-slate-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-auto space-y-2">
              {recent_transactions.length === 0 ? (
                <p className="text-slate-500 text-sm">Keine Einträge</p>
              ) : recent_transactions.map((transaction)=> {
                const tone = txIconTone(transaction.type);
                return (
                <div key={transaction.id} className="flex items-center justify-between p-3 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 border flex items-center justify-center shrink-0 ${tone.box}`}>
                      {transaction.type === 'interest_payment' && <Euro className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'withdrawal' && <ArrowDownRight className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'deposit' && <ArrowUpRight className={`w-4 h-4 ${tone.icon}`} />}
                      {transaction.type === 'bonus' && <Gift className={`w-4 h-4 ${tone.icon}`} />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{txTypeLabel(transaction.type)}</p>
                      <p className="text-xs text-slate-500">{transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('de-DE') : ''}</p>
                    </div>
                  </div>
                  <div className={`text-sm font-medium tabular-nums ${
                    isCredit(transaction.type) ? 'text-success-600' : transaction.type === 'withdrawal' ? 'text-danger-600' : 'text-slate-600'
                  }`}>
                    {isCredit(transaction.type) && '+'}
                    {transaction.amount.toLocaleString('de-DE')} €
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
